import React, { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getErrorMessage } from 'utils'
import { capitalize } from 'utils'

import { useSnackbar } from 'contexts'
import { useReferrerState } from 'hooks'
import { useCustomerMutations, useShopifyConnectionStatus, useShopifyCustomers } from 'hooks/api'
import { PlusIcon, UploadIcon, StoreIcon, PencilIcon } from 'lucide-react'
import { ROUTES } from 'routes'

import { Stack, Typography, Collapse, useTheme } from '@mui/material'

import { Button, Image, Modal } from '@repo/ui'
import { ImportModal, SearchSelect } from 'components'

import {
  ECOMMERCE_INTEGRATIONS,
  DEFAULT_VISIBLE_INTEGRATIONS,
  MethodOption,
  BackRow,
  IntegrationRow,
} from '../AddModal/shared'

interface AddOwnerModalProps {
  company: any
  source?: string // 'owners'
  items?: [] // integration owners
  open: boolean
  createRoute: string // ROUTES.OWNER_CREATE
  searchParams?: {
    fields: string[]
    preview: string
  }
  onClose: () => void
  refetch: () => void
}

// Which screen of the Add owner modal is showing — mirrors AddModal (products):
//  - hub:         the method chooser (Connect a store / Add manually)
//  - integration: browse + select a connected store's customers to import as owners
//  - manual:      Create an owner / Import a file
type AddOwnerMode = 'hub' | 'integration' | 'manual'

export const AddOwnerModal = (props: AddOwnerModalProps) => {
  const {
    company,
    source = 'owners',
    open,
    createRoute,
    searchParams = { fields: ['first_name', 'last_name', 'email'], preview: 'owners' },
    onClose = () => {},
    refetch,
  } = props
  const navigate = useNavigate()
  const referrer = useReferrerState()
  const theme = useTheme()
  const searchSelectRef = useRef(null)

  const [selectedItems, setSelectedItems] = useState([])
  const [openImportModal, setOpenImportModal] = useState(false)
  const [mode, setMode] = useState<AddOwnerMode>('hub')
  const [showAllIntegrations, setShowAllIntegrations] = useState(false)

  const { open: notification } = useSnackbar()

  // Shopify integration
  const { data: connectionStatus } = useShopifyConnectionStatus()
  const isShopifyConnected = connectionStatus?.connected === true

  // Fetch real Shopify customers if connected
  const { data: shopifyCustomersData } = useShopifyCustomers({
    limit: 100,
    offset: 0
  })

  // Only show real Shopify customers (no mock data)
  const integrationOwners = useMemo(() => {
    const owners = []

    if (isShopifyConnected && shopifyCustomersData?.customers) {
      // Add real Shopify customers to the list
      const shopifyOwners = shopifyCustomersData.customers.map(customer => ({
        id: customer.id,
        username: customer.email,
        avatar: null,
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        email: customer.email,
        phone: customer.phone || '',
        status: 'available', // Mark as available for import
        role: { name: 'Owner' },
        integration: customer.integration,
        // Add metadata for creation
        shopify_customer_id: customer.id,
        integration_source: 'SHOPIFY',
        integration_metadata: {
          original_data: customer,
          imported_at: new Date().toISOString(),
        },
      }))

      owners.push(...shopifyOwners)
    }

    return owners
  }, [isShopifyConnected, shopifyCustomersData])

  // Connection status drives the "Connect a store" view: no connected store →
  // show the integrations catalog; connected → show its importable customers.
  const isIntegrationConnected = (name: string) =>
    name.toLowerCase() === 'shopify' ? isShopifyConnected : false
  const hasConnectedStore = ECOMMERCE_INTEGRATIONS.some((i) => isIntegrationConnected(i.name))

  // Most popular first; show the top few, reveal the rest with "View N more".
  const sortedIntegrations = [...ECOMMERCE_INTEGRATIONS].sort((a, b) => b.installs - a.installs)
  const defaultIntegrations = sortedIntegrations.slice(0, DEFAULT_VISIBLE_INTEGRATIONS)
  const extraIntegrations = sortedIntegrations.slice(DEFAULT_VISIBLE_INTEGRATIONS)
  const hasMoreIntegrations = extraIntegrations.length > 0

  const renderIntegrationRow = (integration: (typeof ECOMMERCE_INTEGRATIONS)[number]) => (
    <IntegrationRow
      key={integration.id}
      integration={integration}
      connected={isIntegrationConnected(integration.name)}
      onClick={() => {
        handleClose()
        // Same cross-surface jump as the products AddModal: People -> integration setup,
        // and back to the list the user was building.
        navigate(ROUTES.SETTINGS_COMPANY_INTEGRATION(integration.id), { state: referrer })
      }}
    />
  )

  const formatOwnerSuccessMessage = (count: number) => {
    const noun = count === 1 ? 'owner' : 'owners'
    const verb = count === 1 ? 'has' : 'have'
    const prefix = count === 1 ? '1' : `${count}`
    return `${prefix} ${noun} ${verb} been successfully created`
  }

  const [{ create }, { isLoading }] = useCustomerMutations({
    onSuccess: () => {
      const selectedCount = selectedItems.length
      handleClose()
      refetch()
    },
    onError: error => {
      const message = getErrorMessage(error)
      notification({
        type: 'error',
        title: message,
      })
    },
  })

  const handleCreate = () => {
    if (!company) return
    // Create selected integration owners
    selectedItems.forEach((item) => {
      const customerData: any = {
        email: item.email,
        first_name: item.first_name,
        last_name: item.last_name,
        phone: item.phone || null,
      }

      // Add Shopify metadata if this is a Shopify customer
      if (item.integration_source === 'SHOPIFY') {
        customerData.shopify_customer_id = item.shopify_customer_id
        customerData.integration_source = item.integration_source
        customerData.integration_metadata = item.integration_metadata
      }

      create(customerData)
    })
  }

  const handleImport = () => {
    setOpenImportModal(false)
  }

  const handleClear = () => {
    setSelectedItems([])
    searchSelectRef.current?.handleClear()
  }

  const handleClose = () => {
    setSelectedItems([])
    setOpenImportModal(false)
    setMode('hub')
    setShowAllIntegrations(false)
    onClose()
  }

  const handleSecondaryAction = (item) => {
    if ('integration' in item) {
      return (
        <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: 45,
          px: 2,
          pr: 3,
          py: 0,
          maxWidth: '200px',
          overflow: 'hidden'
        }}>
          <Image
          src={item.integration?.logo}
          alt={item.integration?.name}
          fit="cover"
          sx={{borderRadius: 2, p: 1, my: 1, width: 28, height: 28, flexShrink: 0}} />
          <Typography
            variant="body3"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0
            }}
          >
            {item.integration?.name}
          </Typography>
          </Stack>
      )
    } else {
      return null
    }
  }

  const ownerCount = selectedItems.length
  const isMultipleOwners = ownerCount > 1
  const ownerLabel = isMultipleOwners ? 'owners' : 'owner'
  const addButtonLabel = isMultipleOwners ? `Add ${ownerCount} ${capitalize(ownerLabel)}` : `Add ${capitalize(ownerLabel)}`
  const modalTitle = 'Add owners'

  // The Add primary only belongs to the integration browse view (where items are
  // selected). The hub + manual screens navigate/branch on row click, so they
  // carry no footer.
  const showIntegrationActions =
    mode === 'integration' && !openImportModal && hasConnectedStore && integrationOwners.length > 0

  return (
    <Modal
      title={modalTitle}
      open={open}
      onClose={handleClose}
      sx={{ '& .MuiDialog-paper': { maxWidth: '90vw', width: '100%' } }}
      // The integration import view is a scroll LIST, not a form — trim the Modal's
      // generous bottom padding so the list butts close to the footer, same as the
      // products Add modal. Other views (the method-chooser rows) keep the default.
      bodySx={showIntegrationActions ? { pb: 3 } : undefined}
      actions={
        showIntegrationActions ? (
          <Stack direction="row" sx={{ minWidth: '100%' }} justifyContent="flex-end">
            <Button
              disableElevation
              onClick={() => handleCreate()}
              disabled={selectedItems.length == 0 || isLoading}
            >
              {addButtonLabel}
            </Button>
          </Stack>
        ) : undefined
      }
    >
      {/* HUB — choose how to add */}
      {mode === 'hub' && !openImportModal && (
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            How would you like to add {source}?
          </Typography>
          <MethodOption
            icon={<StoreIcon strokeWidth={1} size={20} />}
            title="Connect a store"
            description="Sync owners automatically from Shopify and other integrations."
            onClick={() => setMode('integration')}
          />
          <MethodOption
            icon={<PencilIcon strokeWidth={1} size={20} />}
            title="Add manually"
            description="Create an owner yourself, or import a list from a file."
            onClick={() => setMode('manual')}
          />
        </Stack>
      )}

      {/* MANUAL — create one or import a file */}
      {mode === 'manual' && !openImportModal && (
        <Stack spacing={3}>
          <BackRow onBack={() => setMode('hub')} />
          <MethodOption
            icon={<PlusIcon strokeWidth={1} size={20} />}
            title="Create an owner"
            description="Add a single owner with full details."
            onClick={() => navigate(createRoute)}
          />
          <MethodOption
            icon={<UploadIcon strokeWidth={1} size={20} />}
            title="Import a file"
            description="Bulk-add owners from a CSV file."
            onClick={() => setOpenImportModal(true)}
          />
        </Stack>
      )}

      {/* INTEGRATION — browse + select a connected store's customers to import */}
      {mode === 'integration' && !openImportModal && (
        <Stack spacing={3}>
          <BackRow
            onBack={() => {
              handleClear()
              setMode('hub')
            }}
          />

          {!hasConnectedStore ? (
            // No store connected yet — show the most-popular integrations to connect
            // one; "View more" reveals the rest.
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Connect a store to import its customers automatically.
              </Typography>
              <Stack spacing={2}>
                {defaultIntegrations.map(renderIntegrationRow)}
              </Stack>
              {hasMoreIntegrations && (
                <Collapse in={showAllIntegrations} timeout={300}>
                  <Stack spacing={2}>
                    {extraIntegrations.map(renderIntegrationRow)}
                  </Stack>
                </Collapse>
              )}
              {hasMoreIntegrations && (
                <Button
                  variant="tertiary"
                  disableElevation
                  onClick={() => setShowAllIntegrations((prev) => !prev)}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {showAllIntegrations ? 'View less' : `View ${extraIntegrations.length} more`}
                </Button>
              )}
            </Stack>
          ) : integrationOwners.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              No customers to import from your connected store yet.
            </Typography>
          ) : (
            <SearchSelect
              items={integrationOwners}
              ref={searchSelectRef}
              onChange={(items) => setSelectedItems(items)}
              field={source}
              showClear={false}
              listItemSecondaryAction={handleSecondaryAction}
              searchParams={searchParams}
            />
          )}
        </Stack>
      )}

      {openImportModal && (
        <ImportModal
          source={source}
          open={true}
          onClose={() => setOpenImportModal(false)}
          refetch={refetch}
          onImport={() => {
            handleImport()
            handleClose()
          }}
        />
      )}
    </Modal>
  )
}
